import stringSimilarity from 'string-similarity'; // Ensure correct import

// Helper function to safely access nested properties
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== 'undefined' ? acc[key] : null), obj);
};

// Helper function to calculate percentage difference between two amounts
const percentageDifference = (amount1, amount2) => {
  if (amount1 === 0 || amount2 === 0) return 100; // Prevent division by zero
  return Math.abs((amount1 - amount2) / ((amount1 + amount2) / 2)) * 100;
};

// Helper function to calculate date difference in days
const dateDifferenceInDays = (date1, date2) => {
  if (!date1 || !date2) return Number.MAX_SAFE_INTEGER;
  const dayInMilliseconds = 24 * 60 * 60 * 1000;
  const diff = Math.abs(new Date(date1) - new Date(date2));
  return diff / dayInMilliseconds;
};

// Normalize merchant names by removing special characters and converting to lowercase
export function normalizeMerchantName(input) {
  return input ? input.replace(/[-'\s]/g, '').toLowerCase() : "";
}

function trimString(input, n) {
  return input.substring(0, n);
}

// Updated function to calculate the similarity score based on revised criteria
const calculateMatchingScore = (item1, item2, config) => {
  let score = 0;
  let breakdown = {}; // To keep track of score contribution from each field

  const amount1 = parseFloat(getNestedValue(item1, config.amountField1)) || 0;
  const amount2 = parseFloat(getNestedValue(item2, config.amountField2)) || 0;
  const account1 = getNestedValue(item1, config.accountField1);
  const account2 = getNestedValue(item2, config.accountField2);
  const date1 = getNestedValue(item1, config.dateField1);
  const date2 = getNestedValue(item2, config.dateField2);
  const categoryId = getNestedValue(item2, 'categoryId');

  const merchant2 = normalizeMerchantName(getNestedValue(item2, config.merchantField2));
  const merchant2length = merchant2.length;
  const merchant1 = trimString(normalizeMerchantName(getNestedValue(item1, config.merchantField1)), merchant2length);

  // Strict match for account number
  if (account1 !== account2) return 0;

  // Determine the amount tolerance to use based on categoryId
  const useTolerance = config.categoryIds && config.categoryIds.includes(categoryId);
  const tolerancePercentage = useTolerance ? config.amountTolerancePercentage : 0;

  // Calculate amount difference and assign score based on tolerance
  const amountDiff = percentageDifference(amount1, amount2);
  if (amount1 === amount2) {
    score += config.amountWeight;
    breakdown.amountMatch = config.amountWeight;
  } else if (amountDiff <= tolerancePercentage) {
    const partialScore = config.amountWeight / 3; // Less weight for amount tolerance match
    score += partialScore;
    breakdown.amountMatch = partialScore;
  } else {
    return 0; // If amount difference is too large, not a match
  }

  // Calculate score based on date proximity
  if (date1 && date2 && dateDifferenceInDays(date1, date2) <= config.dateProximity) {
    score += config.dateWeight;
    breakdown.dateProximity = config.dateWeight;
  } else {
    score -= config.dateWeight / 2; // Apply penalty if date proximity is outside the acceptable range
    breakdown.datePenalty = config.dateWeight / 2;
  }

  // Calculate score based on merchant name similarity, but only if similarity is above a threshold
  let merchantScore = 0;
  if (merchant1 && merchant2) {
    const similarity = stringSimilarity.compareTwoStrings(merchant1, merchant2);

    if (similarity >= config.minimumMerchantSimilarity) {
      merchantScore = Math.floor(similarity * config.merchantWeight);
      score += merchantScore;
    }

    breakdown.merchantSimilarity = merchantScore;

    if (similarity < 0.5) {
      const penalty = config.merchantWeight / 2;
      score -= penalty;
      breakdown.merchantPenalty = penalty;
    }
  }

  return score;
};

// Original function to find matches between array1 and array2
// array1 receipts, array2 vendors
export const findMatches = (array1, array2, config = {}) => {
  // console.warn('array1: ', array1);  // receipts
  // console.warn('array2: ', array2);  // transactions
  const defaultConfig = {
    amountField1: 'capitalOneInstantNotification.purchaseAmount',
    amountField2: 'amount',
    accountField1: 'capitalOneInstantNotification.lastFour',
    accountField2: 'accountNumber',
    dateField1: 'capitalOneInstantNotification.purchaseDate',
    dateField2: 'date',
    merchantField1: 'capitalOneInstantNotification.purchaseMerchant',
    merchantField2: 'merchantEntityName',
    idField1: 'plaidCreditCardTransactionId', // New field for direct matching
    idField2: 'id', // New field for direct matching

    amountWeight: 25,
    accountWeight: 25,
    dateWeight: 15,
    dateProximity: 2,
    merchantWeight: 0.1,

    amountTolerancePercentage: 1,
    minimumScoreThreshold: 1,

    // Separate similarity configurations for merchant and vendor matching
    // minimumMerchantSimilarity: 0.7,  // Keep this for merchantEntityName matching
    // minimumQbVendorSimilarity: 0.4,  // New value for qbVendor matching to loosen it up
    minimumMerchantSimilarity: 1,  // Keep this for merchantEntityName matching
    minimumQbVendorSimilarity: 1,  // Must be 1 for exact match

    categoryIds: [], // Add default for categoryIds
  };

  const finalConfig = { ...defaultConfig, ...config };

  const matches = [];
  const unmatchedFromArray1 = [];
  const unmatchedFromArray2 = new Set(array2.map((item) => item.id));

  // Store the IDs of items from array2 that were matched with array1 items
  const matchedFromArray2Ids = new Set();

  // A transaction belongs to at most one receipt. A transaction counts as
  // taken when it already points at a receipt, or when an earlier receipt in
  // this pass claimed it. Without this, two receipts could both show as
  // matched to the same transaction and both be approved (Oct 2026: two
  // QuikTrip $17.13 receipts on one transaction, one left stranded).
  const linkedReceiptId = (tx) => tx?.capitalOneCreditCardTransactionReceiptId || null;
  const claimedTxnIds = new Set();
  // Transactions some receipt already points at directly. Fuzzy matching must
  // not hand these to a different receipt, whatever order receipts arrive in.
  const reservedForDirect = new Set(
    array1.map((r) => getNestedValue(r, finalConfig.idField1)).filter(Boolean)
  );

  array1.forEach((item1) => {
    let bestMatch = null;
    let highestScore = 0;

    // Check for direct match using idField1 and idField2
    // item2 matchStatus = 'ready-for-approval'
    // transactionId === receipts.plaidCreditCardTransactionId
    const id1 = getNestedValue(item1, finalConfig.idField1);
    const directMatch = id1 ? array2.find((item2) => 
      // item2[finalConfig.idField2] === id1 && 
      // item2.matchStatus === 'ready-for-approval'
      item2[finalConfig.idField2] === id1
    ) : null;
    
    if (directMatch) {
      // console.log('📌 >>>>> directMatch: ', directMatch?.amount, id1, directMatch);
      // console.log('📌 >>>>> item1: ', item1);

      // The receipt points at this transaction, but the transaction may already
      // belong to a different receipt (or another receipt in this pass claimed
      // it first). Still listed, so nothing disappears from the Ready tab, but
      // flagged so it can be shown and publishing refuses it.
      const otherReceipt = linkedReceiptId(directMatch);
      // If the transaction points at a receipt, that receipt owns it. If it
      // points at none, the first receipt in this pass to claim it wins.
      const conflict = otherReceipt
        ? otherReceipt !== item1.id
        : claimedTxnIds.has(directMatch.id);
      if (conflict) {
        console.warn('⚠️ Direct match conflict: transaction already belongs to another receipt', {
          receiptId: item1.id, transactionId: directMatch.id, linkedReceiptId: otherReceipt,
        });
      }

      matches.push({
        ...item1,
        matchedItem: directMatch,
        matchingScore: Infinity, // Indicate a perfect match
        matchType: 'direct', // Label this as a direct match
        ...(conflict && { matchConflict: 'transaction-has-another-receipt' }),
      });
      claimedTxnIds.add(directMatch.id);
      unmatchedFromArray2.delete(directMatch.id);
      return; // Skip further processing for this item1
    }

    // if (item1?.matchStatus === 'preemptive-invoice') {
      // console.log('📌📌📌 >>>>> item1: ', item1);
    //   matches.push({
    //     ...item1,
    //     matchedItem: null,
    //     matchingScore: 0, // Indicate a perfect match
    //     matchType: 'preemptive-invoice', // Label this as a direct match
    //   })
    // }

    array2.forEach((item2) => {
      // Never fuzzy-match onto a transaction that already has a receipt or
      // was claimed earlier in this pass. Same guard as the notification path.
      const linked = linkedReceiptId(item2);
      if ((linked && linked !== item1.id) || claimedTxnIds.has(item2.id) || reservedForDirect.has(item2.id)) return;

      const score = calculateMatchingScore(item1, item2, finalConfig);

      if (score > highestScore) {
        highestScore = score;
        bestMatch = item2;
      }
    });

    // Consider a match only if it meets the minimum score threshold
    if (bestMatch && highestScore >= finalConfig.minimumScoreThreshold) {
      matches.push({ ...item1, matchedItem: bestMatch, matchingScore: highestScore });
      claimedTxnIds.add(bestMatch.id);
      unmatchedFromArray2.delete(bestMatch.id);
      matchedFromArray2Ids.add(bestMatch.id); // Track matched IDs
    } else {
      unmatchedFromArray1.push(item1);
    }
  });

  // Create the final unmatchedFromArray2, removing items that are matched in unmatchedFromArray1
  const unmatchedFromArray2Array = Array.from(unmatchedFromArray2)
    .filter((id) => !matchedFromArray2Ids.has(id))
    .map((id) => array2.find((item) => item.id === id));

  // const unmatchedFromArray2Array = Array.from(unmatchedFromArray2).map((id) => array2.find((item) => item.id === id));


  return {
    matches,
    unmatchedFromArray1,
    unmatchedFromArray2: unmatchedFromArray2Array,
  };
};

// Vendor-name canonicalizer used ONLY by the vendor-matching / dedup path.
// Deliberately separate from normalizeMerchantName (which is shared by the
// receipt<->transaction scorer and the Capital One notification matcher) so
// this stricter collapsing can't perturb those other matchers.
//
// Collapses common connector tokens to a single canonical form so near-variant
// spellings of the same vendor resolve together:
//   "Diamond Gas N Wash"  -> "diamondgasandwash"
//   "Diamond Gas & Wash"  -> "diamondgasandwash"
//   "Diamond Gas And Wash"-> "diamondgasandwash"
// then strips case and all non-alphanumerics.
// QBO admins tag vendor display names with how the vendor gets paid and with
// legal-entity suffixes — "Cherokee Hose And Supply-Check", "GenServ (ACH)",
// "AFCO - Finance Plan (Auto Pay)", "Rhea Health, LLC (ACH)". Bank merchant
// strings never carry these, so they are stripped (repeatedly, from the END of
// the name only) before canonical/token comparison. Stored names are untouched.
const PAYMENT_SUFFIX_RE =
  /\s*(?:\(\s*(?:ach|e-?check|check|chk|auto\s*-?\s*pay|must\s+be\s+(?:a\s+)?check)\s*\)|[-\u2013\u2014]\s*(?:ach|check|chk))\s*$/i;
const ENTITY_SUFFIX_RE = /[\s,]+(?:inc|llc|l\.l\.c|ltd|corp|corporation|pllc)\.?\s*$/i;

export function stripVendorSuffixes(input) {
  let s = String(input || '').trim();
  for (let i = 0; i < 4; i++) {
    const next = s.replace(PAYMENT_SUFFIX_RE, '').replace(ENTITY_SUFFIX_RE, '').trim();
    if (next === s) break;
    // Never strip a name down to fewer than 3 letters/digits.
    if (next.replace(/[^a-z0-9]/gi, '').length < 3) break;
    s = next;
  }
  return s;
}

export function canonicalizeVendorName(input) {
  if (!input) return "";
  return stripVendorSuffixes(input)
    .toLowerCase()
    .replace(/&/g, " and ")        // ampersand -> and
    .replace(/\+/g, " and ")       // plus -> and
    .replace(/\bn\b/g, " and ")    // standalone "n" -> and (word-bounded only)
    .replace(/[^a-z0-9]+/g, "");   // drop spaces and punctuation
}

// Token form of a vendor name: same suffix stripping and connector collapsing as
// canonicalizeVendorName but it PRESERVES word boundaries instead of stripping
// them. Used by the Tier 2 fallback so matching can require whole-token equality
// rather than raw character substring overlap (which let short names like "UTS"
// match inside "...doNUTS").
export function vendorNameTokens(input) {
  if (!input) return [];
  return stripVendorSuffixes(input)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\+/g, " and ")
    .replace(/\bn\b/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")   // separators -> space (KEEP boundaries)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

// True when token list `a` is a leading subsequence of token list `b`
// (e.g. ["fleetpride"] leads ["fleetpride","okc","ach","statement"]).
function isTokenPrefix(a, b) {
  if (a.length === 0 || a.length > b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// Canonical/token forms are computed once per vendor list, not once per lookup:
// the render path calls the matcher for every receipt against ~4,600 vendors.
const vendorIndexCache = new WeakMap();
function getVendorIndex(qbVendors) {
  let idx = vendorIndexCache.get(qbVendors);
  if (!idx) {
    idx = qbVendors.map((v) => ({
      vendor: v,
      canonical: canonicalizeVendorName(v?.qbDisplayName),
      tokens: vendorNameTokens(v?.qbDisplayName),
    }));
    vendorIndexCache.set(qbVendors, idx);
  }
  return idx;
}

/**
 * THE vendor-name matcher. Every place that pairs a merchant/vendor name with a
 * QBO vendor (fuzzy enrichment, render-time enrichment, publish dedup guard)
 * goes through this so they can no longer disagree.
 *
 * Returns { qbVendor, tier, score, candidates }:
 *   tier 1 (score 100) — exact canonical match after suffix stripping
 *                        ("Cherokee Hose & Supply" == "Cherokee Hose And Supply-Check")
 *   tier 2 (score 90)  — whole-token prefix either direction
 *                        ("Lowes" ~ "Lowes Home Improvement #4471")
 *   tier 0 (score 0)   — no match
 * Callers decide how much to trust tier 2; this function only reports it.
 */
export function matchQbVendorByName(name, qbVendors) {
  const none = { qbVendor: null, tier: 0, score: 0, candidates: [] };
  if (!name || !Array.isArray(qbVendors) || qbVendors.length === 0) return none;

  const targetCanonical = canonicalizeVendorName(name);
  if (!targetCanonical) return none;

  const index = getVendorIndex(qbVendors);

  let tier = 1;
  let hits = index.filter((e) => e.canonical === targetCanonical);

  if (hits.length === 0) {
    tier = 2;
    const targetTokens = vendorNameTokens(name);
    hits = targetTokens.length
      ? index.filter((e) => isTokenPrefix(targetTokens, e.tokens) || isTokenPrefix(e.tokens, targetTokens))
      : [];
  }

  if (hits.length === 0) return none;

  // Rank among multiple hits: most specific (longest) display name, then
  // highest qbId as a stable tiebreak. With the reconcile in place the mirror
  // only holds QBO-active vendors in the matchable set, so duplicates here are
  // genuine QBO-side duplicates, not stale rows.
  const candidates = hits
    .map((e) => e.vendor)
    .sort((a, b) => {
      const lenDiff = (b.qbDisplayName?.length || 0) - (a.qbDisplayName?.length || 0);
      if (lenDiff !== 0) return lenDiff;
      return (parseInt(b.qbId, 10) || 0) - (parseInt(a.qbId, 10) || 0);
    });

  if (candidates.length > 1) {
    console.warn(
      `⚠️ ${candidates.length} vendor records match "${name}" (tier ${tier}, canonical "${targetCanonical}"):`,
      candidates.map((v) => ({ qbId: v.qbId, name: v.qbDisplayName }))
    );
  }

  return { qbVendor: candidates[0], tier, score: tier === 1 ? 100 : 90, candidates };
}

const findMatchingQbVendor = (matchedItem, qbVendors) => {
  const { qbVendor, score } = matchQbVendorByName(matchedItem?.merchantEntityName, qbVendors);
  return { qbVendor, qbVendorMatchScore: qbVendor ? score : 0 };
};

// New function to find matches and include qbVendors using name similarity, along with the match score
// array1 receipts, array2 vendors
export const findMatchesWithQbVendors = (array1, array2, qbVendors, config = {}) => {
  console.warn('receiptsNeedingMatch: ', array1);  // receipts
  console.warn('syncedTransactions: ', array2);  // transactions
  // console.warn('qbVendors: ', qbVendors); // vendors

  // Run the original matching function to get matches and unmatched items
  const { matches, unmatchedFromArray1, unmatchedFromArray2 } = findMatches(array1, array2, config);
  // Enrich each match with the corresponding qbVendor and include qbVendorMatchScore

  // console.log('matches: ', matches);

  const enrichedMatches = matches.map((match) => {
    const matchedItem = match?.matchedItem;

    if (!matchedItem) {
      console.warn("⚠️ Skipping match due to missing matchedItem:", match);
      return match;
    }

    const { qbVendor, qbVendorMatchScore } = findMatchingQbVendor(matchedItem, qbVendors, config);
    const isCredit = matchedItem.amount < 0;

    // 🧠 TransactionType logic applied directly to the flattened receipt (i.e., the match itself)
    return {
      ...match,
      qbVendor,
      qbVendorMatchScore,
      transactionType: match.transactionType || (isCredit ? "credit" : undefined),
    };
    // return {
    //   ...match,
    //   qbVendor,
    //   qbVendorMatchScore,
    //   transactionType: isCredit
    //     ? "credit"
    //     : typeof match.transactionType === "string"
    //       ? match.transactionType
    //       : undefined,
    // };
  });

  return {
    matches: enrichedMatches,
    unmatchedFromArray1,
    unmatchedFromArray2,
  };
};

/**
 * Notification-direct match: pairs `with-notification` and `with-notification-admin`
 * receipts to their transactions using card + amount + date, bypassing fuzzy
 * matching. The cardholder's tap on the notification link is a stronger signal
 * than any string similarity computation.
 *
 * Returns array of receipts with { matchedItem, matchingScore: Infinity,
 * matchType: 'notification-direct', flag }. `flag` is one of:
 *   null            — clean pair, exact amount, merchants align
 *   'weakMerchant'  — exact amount/card/date but merchant strings disagree
 *                     (Plaid's inconsistent naming, e.g. PILOT_00460 vs Pilot Flying J)
 *   'tipSuspected'  — restaurant category, transaction > notification by > tolerance
 *                     (likely the tip was added between swipe and post)
 */
export function findNotificationDirectMatches(uploadedReceipts, syncedTransactions, options = {}) {
  const opts = {
    notificationStatuses:           ['with-notification', 'with-notification-admin'],
    dateProximityDays:              5,
    restaurantCategoryIds:          ['13005000', '13001000'],
    tipMaxIncreasePercent:          40,    // restaurant txn may exceed notif by up to this %
    cleanAmountTolerancePercent:    1,     // anything ≤ this counts as exact-amount
    minimumMerchantSimilarityForClean: 0.8,
    ...options,
  };

  const dayMs = 86400000;

  // Index transactions by card last-four for fast lookup
  const txnsByCard = new Map();
  for (const tx of syncedTransactions) {
    if (!tx.accountNumber) continue;
    if (!txnsByCard.has(tx.accountNumber)) txnsByCard.set(tx.accountNumber, []);
    txnsByCard.get(tx.accountNumber).push(tx);
  }

  // Track which transactions get claimed by this pass so two receipts don't
  // claim the same transaction.
  const claimedTxnIds = new Set();
  const results = [];

  for (const receipt of uploadedReceipts) {
    if (!opts.notificationStatuses.includes(receipt.matchStatus)) continue;
    
    // Already linked to a transaction — the pairing is decided, don't re-derive one.
      if (receipt.plaidCreditCardTransactionId) continue;

    const notif = receipt.capitalOneInstantNotification;
    if (!notif || !notif.lastFour || !notif.purchaseDate || notif.purchaseAmount == null) {
      console.warn('⚠️ Notification-direct: receipt has notification status but missing notification data — falling through:',
        { receiptId: receipt.id, matchStatus: receipt.matchStatus });
      continue;
    }

    const notifAmount = parseFloat(notif.purchaseAmount);
    if (isNaN(notifAmount)) continue;

    // const candidates = (txnsByCard.get(notif.lastFour) || []).filter(tx => {
    //   if (claimedTxnIds.has(tx.id)) return false;
    //   if (!tx.date) return false;
    //   const dDays = Math.abs(new Date(tx.date) - new Date(notif.purchaseDate)) / dayMs;
    //   return dDays <= opts.dateProximityDays;
    // });
    const candidates = (txnsByCard.get(notif.lastFour) || []).filter(tx => {
      // Transactions keep status `pending_match` after a receipt is linked and only
      // change at publish, so a linked transaction is still in syncedTransactions.
      // Mirrors the guard the Auto Match path has always had.
      if (tx.capitalOneCreditCardTransactionReceiptId) return false;
      if (claimedTxnIds.has(tx.id)) return false;
      if (!tx.date) return false;
      const dDays = Math.abs(new Date(tx.date) - new Date(notif.purchaseDate)) / dayMs;
      return dDays <= opts.dateProximityDays;
    });

    if (!candidates.length) continue;

    let best = null;
    let bestScore = -Infinity;

    for (const tx of candidates) {
      const txAmount = parseFloat(tx.amount);
      if (isNaN(txAmount)) continue;

      const amountDiffPct = txAmount === notifAmount
        ? 0
        : Math.abs((txAmount - notifAmount) / ((txAmount + notifAmount) / 2)) * 100;

      const isRestaurant = opts.restaurantCategoryIds.includes(tx.categoryId);
      const cleanAmount  = amountDiffPct <= opts.cleanAmountTolerancePercent;
      const tipCapHit    = !cleanAmount && isRestaurant
                           && txAmount > notifAmount
                           && amountDiffPct <= opts.tipMaxIncreasePercent;

      // Hard filter: must be either clean amount OR plausibly a tip
      if (!cleanAmount && !tipCapHit) continue;

      // Score: prefer exact-amount, then closer dates
      const dateDays = Math.abs(new Date(tx.date) - new Date(notif.purchaseDate)) / dayMs;
      const score = (cleanAmount ? 1000 : 500) - (dateDays * 10);

      if (score > bestScore) {
        bestScore = score;
        best = { tx, cleanAmount, tipCapHit };
      }
    }

    if (!best) continue;

    // Decide the flag
    let flag = null;
    if (best.tipCapHit) {
      flag = 'tipSuspected';
    } else if (best.cleanAmount) {
      // Even on clean amounts, warn if merchant strings disagree (the Pilot case)
      const m2 = normalizeMerchantName(best.tx.merchantEntityName);
      const m1raw = normalizeMerchantName(notif.purchaseMerchant);
      if (m1raw && m2) {
        const m1 = trimStringLocal(m1raw, m2.length);
        const sim = stringSimilarity.compareTwoStrings(m1, m2);
        if (sim < opts.minimumMerchantSimilarityForClean) {
          flag = 'weakMerchant';
        }
      }
    }

    claimedTxnIds.add(best.tx.id);
    results.push({
      ...receipt,
      matchedItem:   best.tx,
      matchingScore: Infinity,
      matchType:     'notification-direct',
      flag,
    });
  }

  return results;
}

// Local copy — same logic as the trimString function above. Kept local
// to avoid exporting an internal helper.
function trimStringLocal(input, n) { return input.substring(0, n); }

// Example usage:
// const uploadedReceipts = /* Your first array here */;
// const syncedTransactions = /* Your second array here */;
// const qbVendors = /* Your qbVendors array here */;
// const config = {
//   amountField1: 'capitalOneInstantNotification.purchaseAmount',
//   amountField2: 'amount',
//   accountField1: 'capitalOneInstantNotification.lastFour',
//   accountField2: 'accountNumber',
//   dateField1: 'capitalOneInstantNotification.purchaseDate',
//   dateField2: 'date',
//   merchantField1: 'capitalOneInstantNotification.purchaseMerchant',
//   merchantField2: 'merchantEntityName',
//   amountWeight: 25,
//   accountWeight: 25,
//   dateWeight: 15,
//   dateProximity: 2,
//   merchantWeight: 0.1,
//   amountTolerancePercentage: 1,
//   minimumScoreThreshold: 1,
//   minimumMerchantSimilarity: 0.7,
//   minimumQbVendorSimilarity: 0.4,
//   categoryIds: [], // Add default for categoryIds
// };

// const result = findMatchesWithQbVendors(uploadedReceipts, syncedTransactions, qbVendors, config);
// console.log(result);
